import { useMemo } from 'react';

import EntityIcon, { type EntityIconVariant } from './EntityIcon';
import GroupBarRow from './GroupBarRow';
import SidebarCard from '../ui/SidebarCard';
import {
  useExplorationActions,
  useExplorationState,
} from '../../context/ExplorationContext';
import { buildAttachedVolumeRows } from '../../lib/sidebar/buildAttachedVolumeRows';
import {
  buildActivationGroupBarSegments,
  buildCountBreakdownData,
  type BreakdownData,
} from '../../lib/sidebar/buildBreakdownData';
import { computeTopLevelGroups, type TopLevelGroup } from '../../lib/sidebar/computeTopLevelGroups';
import { buildListingGroupHighlight } from '../../lib/sidebar/listingGroupHighlight';
import {
  iconTypeForListingGroup,
  type ListingGroupIconTypeOptions,
} from '../../lib/sidebar/listingGroupIconType';
import {
  currentInnerPalette,
  isStatusDrillLevel,
  resolveGroupBarsHeading,
  type SidebarGroupBarsMode,
} from '../../lib/sidebar/sidebarPalettes';
import { nextLocationForListingGroup } from '../../lib/sidebar/nextLocationForListingGroup';
import { viewSignalBarColor } from '../../lib/sidebar/signalColors';
import { volumesInCurrentScope } from '../../lib/sidebar/volumesInCurrentScope';
import {
  buildCoverageGroups,
  enrichCoverageWrappers,
} from '../../lib/coverage/buildCoverageGroups';
import { buildCoverageInstances } from '../../lib/coverage/classifyInstance';
import {
  COVERAGE_INSTALL_COLORS,
  COVERAGE_INSTALL_LABELS,
  COVERAGE_INSTALL_ORDER,
} from '../../lib/coverage/constants';
import type { CoverageWrapper } from '../../lib/coverage/types';
import type { ExplorationVolume } from '../../types/exploration';
import Typography from '~/shared/components/Typography';

export type { SidebarGroupBarsMode };

interface SidebarGroupBarsCardProps {
  /** Full filtered volume set — used to resolve drill scope for group bars. */
  volumes: ExplorationVolume[];
  /** Pre-scoped volumes for attached-volume rows (optional; derived when omitted). */
  scopedVolumes?: ExplorationVolume[];
  /** One GroupBarRow per volume; ignores drill-path grouping. */
  mode?: SidebarGroupBarsMode;
  headingOverride?: string;
}

function flattenWrapperInstances(wrapper: CoverageWrapper) {
  return wrapper.cards.flatMap((card) => card.instances);
}

function buildInstallBreakdown(wrapper: CoverageWrapper): BreakdownData {
  const instances = flattenWrapperInstances(wrapper);
  const sizeWeights: Partial<Record<(typeof COVERAGE_INSTALL_ORDER)[number], number>> = {};
  const counts: Partial<Record<(typeof COVERAGE_INSTALL_ORDER)[number], number>> = {};

  instances.forEach((inst) => {
    counts[inst.install] = (counts[inst.install] ?? 0) + 1;
    sizeWeights[inst.install] = (sizeWeights[inst.install] ?? 0) + (inst.supportedSize || 0);
  });

  return buildCountBreakdownData(
    counts,
    COVERAGE_INSTALL_ORDER,
    (k) => COVERAGE_INSTALL_LABELS[k],
    (k) => COVERAGE_INSTALL_COLORS[k],
    sizeWeights
  );
}

export default function SidebarGroupBarsCard({
  volumes,
  scopedVolumes,
  mode = 'exploration',
  headingOverride,
}: SidebarGroupBarsCardProps) {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const location = state.drillPath;
  const inCoverage = state.view === 'coverage';
  const inActivation = state.view === 'activation';
  const atStatusDrill = isStatusDrillLevel(location);
  const showAttachedVolumes = mode === 'attachedVolumes' || (!inCoverage && atStatusDrill);

  const attachedVolumes = useMemo(() => {
    if (!showAttachedVolumes) return [];
    if (mode === 'attachedVolumes') return scopedVolumes ?? volumes;
    if (scopedVolumes) return scopedVolumes;
    return volumesInCurrentScope(volumes, {
      secondary: state.secondary,
      location,
      groupByAccount: state.groupByAccount,
      tagKey: state.tagKey,
      view: state.view,
    });
  }, [
    showAttachedVolumes,
    mode,
    volumes,
    scopedVolumes,
    state.secondary,
    location,
    state.groupByAccount,
    state.tagKey,
    state.view,
  ]);

  const header = resolveGroupBarsHeading(
    state,
    showAttachedVolumes ? 'attachedVolumes' : mode,
    headingOverride
  );
  const palette = useMemo(() => currentInnerPalette(state.view), [state.view]);
  const signalKey = palette.order[0];
  const signalColor = viewSignalBarColor(state.view);

  const listingGroupOpts = useMemo(
    (): ListingGroupIconTypeOptions => ({
      secondary: state.secondary,
      location,
      groupByAccount: state.groupByAccount,
      view: state.view,
      tagKey: state.tagKey,
    }),
    [state.secondary, location, state.groupByAccount, state.view, state.tagKey]
  );

  const attachedVolumeRows = useMemo(() => {
    if (!showAttachedVolumes) return [];
    return buildAttachedVolumeRows(attachedVolumes, palette, signalKey);
  }, [showAttachedVolumes, attachedVolumes, palette, signalKey]);

  const explorationGroups = useMemo(() => {
    if (showAttachedVolumes || inCoverage) return [];
    return computeTopLevelGroups(volumes, {
      secondary: state.secondary,
      location,
      groupByAccount: state.groupByAccount,
      tagKey: state.tagKey,
      view: state.view,
    });
  }, [
    volumes,
    showAttachedVolumes,
    inCoverage,
    state.secondary,
    location,
    state.groupByAccount,
    state.tagKey,
    state.view,
  ]);

  const installationGroups = useMemo(() => {
    if (showAttachedVolumes || !inCoverage) return [];
    const instances = buildCoverageInstances(volumes);
    const groups = buildCoverageGroups(instances, state.coverageGrouping, state.groupByAccount);
    return enrichCoverageWrappers(groups);
  }, [volumes, showAttachedVolumes, inCoverage, state.coverageGrouping, state.groupByAccount]);

  const maxExplorationSize = useMemo(
    () => Math.max(...explorationGroups.map((g) => g.totalSize), 1),
    [explorationGroups]
  );

  const explorationRows = useMemo(
    () =>
      explorationGroups.map((group) => {
        const signalPct = inActivation
          ? 0
          : (() => {
              const signalSize = group.vols.reduce(
                (s, v) => (v[palette.field] === signalKey ? s + (v.size || 0) : s),
                0
              );
              return group.totalSize > 0 ? (signalSize / group.totalSize) * 100 : 0;
            })();
        const iconType = iconTypeForListingGroup(group.name, listingGroupOpts);
        const segments = inActivation
          ? buildActivationGroupBarSegments(group.vols, palette)
          : undefined;
        return {
          group,
          signalPct,
          segments,
          lengthRatio: group.totalSize / maxExplorationSize,
          iconType,
        };
      }),
    [
      explorationGroups,
      maxExplorationSize,
      inActivation,
      palette,
      signalKey,
      listingGroupOpts,
    ]
  );

  const coverageRows = useMemo(
    () =>
      installationGroups.map((group) => {
        const breakdown = buildInstallBreakdown(group);
        const signalSeg = breakdown.segments[0];
        const signalPct = signalSeg?.flexPct ?? 0;
        return { group, signalPct, lengthRatio: 1 };
      }),
    [installationGroups]
  );

  const canDrill = !inCoverage && !atStatusDrill;

  const handleGroupDrill = (group: TopLevelGroup, iconType: EntityIconVariant | null) => {
    if (!canDrill) return;
    actions.setDrillPath(nextLocationForListingGroup(location, group.name, iconType, state.view));
  };

  const groups = showAttachedVolumes
    ? attachedVolumeRows
    : inCoverage
      ? installationGroups
      : explorationGroups;

  if (showAttachedVolumes) {
    if (!attachedVolumes.length) return null;

    return (
      <SidebarCard variant="interactive" heading={header}>
        {attachedVolumeRows.map(({ volume, name, totalSize }) => (
          <GroupBarRow
            key={volume.ebsId}
            name={name}
            totalSize={totalSize}
            icon={<EntityIcon variant="volume" />}
            showSizeBar={false}
            clickable
            onClick={() => actions.selectVolume(volume)}
            onMouseEnter={() => actions.setHighlight({ kind: 'volume', ebsId: volume.ebsId })}
            onMouseLeave={() => actions.setHighlight(null)}
            onFocus={() => actions.setHighlight({ kind: 'volume', ebsId: volume.ebsId })}
            onBlur={() => actions.setHighlight(null)}
          />
        ))}
      </SidebarCard>
    );
  }

  return (
    <SidebarCard variant="interactive" heading={header}>
      {!groups.length ? (
        <Typography variant="caption" color="text-gray-700" className="px-3 py-2">
          No groups
        </Typography>
      ) : inCoverage ? (
        coverageRows.map(({ group, signalPct, lengthRatio }) => (
          <GroupBarRow
            key={group.name}
            name={group.name}
            totalSize={group.supportedSize ?? 0}
            totalCount={group.totalInstances ?? 0}
            signalPct={signalPct}
            signalColor={signalColor}
            lengthRatio={lengthRatio}
            clickable={false}
          />
        ))
      ) : (
        explorationRows.map(({ group, signalPct, segments, lengthRatio, iconType }) => (
          <GroupBarRow
            key={group.name}
            name={group.name}
            totalSize={group.totalSize}
            totalCount={group.count}
            icon={iconType ? <EntityIcon variant={iconType} /> : undefined}
            segments={segments}
            signalPct={segments?.length ? undefined : signalPct}
            signalColor={segments?.length ? undefined : signalColor}
            lengthRatio={lengthRatio}
            clickable={canDrill}
            onClick={() => handleGroupDrill(group, iconType)}
            onMouseEnter={() =>
              actions.setHighlight(
                buildListingGroupHighlight(group.name, group.vols, listingGroupOpts)
              )
            }
            onMouseLeave={() => actions.setHighlight(null)}
            onFocus={() =>
              actions.setHighlight(
                buildListingGroupHighlight(group.name, group.vols, listingGroupOpts)
              )
            }
            onBlur={() => actions.setHighlight(null)}
          />
        ))
      )}
    </SidebarCard>
  );
}
